/**
 * MigrationArchitectWorkspace.tsx — Asana 1216373781992284, item 4: "Migration Architect needs a
 * tabbed workspace, not one scrolling analysis."
 *
 * Before this, a Migration Architect run rendered as a single long markdown document — per-
 * artifact reasoning, then a consolidated table, then risk flags, all in one scroll — inside
 * Intelligence.tsx's generic per-mode result card. It read fine and demoed badly: nothing was
 * navigable or filterable, and the consolidated summary was buried below the per-artifact detail
 * instead of leading.
 *
 * This restructures it the way Portfolio Reporting is structured (frontend/src/pages/
 * PortfolioReporting.tsx): a tab strip with count badges, header actions (needs-review chip,
 * refresh, Generate Executive Brief) and a persistent context bar.
 *
 * Four of the eight tabs are NOT rebuilt here — they ARE the existing "as built" screens, reused
 * directly rather than duplicated:
 *   - Framework Mapping  -> DispositionFramework.tsx via the embedded LogicDecomposerDashboard.
 *   - Review Queue        -> ReviewQueue.tsx, same path (item 11, spec §5.8/§10.3 — filter, bulk
 *                            select, approve/override, commit). The needs-review chip opens this.
 *   - Consolidation       -> Clusters.tsx, same path.
 *   - Deliverables        -> Phases.tsx, same path (item 14 — the four-phase engagement artifact
 *                            rail, now reading live engagement_artifacts state instead of the
 *                            placeholder that used to render here).
 * LogicDecomposerDashboard already owns a Portfolio grid + per-artifact detail panel
 * (Overview / Lineage / Business Logic+Translate / Insights) that is exactly what item 4's
 * "Artifacts" tab asks for, so it is embedded too, in "controlled view" mode (see the `view` /
 * `hideViewSwitcher` props added to it for this): THIS component's tab strip drives which of its
 * internal views shows, instead of rendering a second, redundant nav bar inside a nav bar. It
 * stays mounted (just hidden) across outer-tab switches so its own state — the open artifact
 * panel, which translate sub-tab is selected — survives navigating Overview/Risks/etc. and back.
 *
 * The "N need review" chip is the real needs-review queue the framework spec already defines
 * (§10.3): status `human_review` already encodes the 0.60-0.85 confidence band AND ties AND gate
 * overturns (dispositionEngine.js sets `forceReview` for all three and `routeByConfidence` turns
 * that into `human_review`); status `provisional` is exactly "unresolved Cat 3." No new logic was
 * invented to compute this — it reads two existing status values.
 *
 * Overview's six-outcome / Cat 1/2/3 strips and the needs-review count reflect the PROJECT's live
 * disposition snapshot (useFrameworkData), not just the files in this one analysis run — that is
 * deliberate: it is the same live state Framework Mapping shows, so the two never disagree.
 *
 * Consolidated-summary table and Risk Flags cards render ONLY when the backend actually returned
 * structured data for them (routes/intelligence.js + services/migrationArchitect/summary.js). A
 * run that predates this change, or one the model didn't structure, falls back to an honest note
 * plus the full narrative — never a fabricated table row or an invented risk.
 *
 * Deliverables is intentionally a "not yet available" placeholder: item 14 (Deliverables tab
 * backend — engagement_artifacts table) has not been built. Showing anything else there would be
 * exactly the kind of invented data this codebase's engineering culture rules out.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  RefreshCw, Download, FileSpreadsheet, Presentation, FileText, Crown, Loader2,
  AlertTriangle, ShieldAlert, ChevronRight, Info,
} from 'lucide-react';
import { useFrameworkData } from '../LogicDecomposer/framework/useFrameworkData';
import LogicDecomposerDashboard from '../LogicDecomposer/LogicDecomposerDashboard';
import type { View as LdView } from '../LogicDecomposer/types';
import type { Outcome, Timing } from '../LogicDecomposer/framework/types';
import CloudPortfolioTab from './CloudPortfolio/CloudPortfolioTab';

export interface RiskFlag {
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  detail: string;
  artifact?: string | null;
}

export interface ConsolidatedSummaryRow {
  artifact: string;
  path?: string | null;
  recommendation: string;
  confidence?: 'High' | 'Medium' | 'Low' | null;
}

export interface MigrationArchitectWorkspaceProps {
  analysisMarkdown: string;
  fileName?: string | null;
  consolidatedSummary: ConsolidatedSummaryRow[] | null;
  riskFlags: RiskFlag[] | null;
  renderMarkdown: (text: string) => ReactNode;
  onExportPdf: () => void;
  onExportExcel: () => void;
  onExportPptx: () => void;
  onExportWord: () => void;
  onSendToCouncil: () => void;
  exportingExcel: boolean;
  exportingPptx: boolean;
  exportingWord: boolean;
  requestedTab?: string | null;
}

type OuterTab =
  | 'overview' | 'cloud-portfolio' | 'rules' | 'artifacts' | 'attestation' | 'framework'
  | 'consolidation' | 'review' | 'deliverables' | 'audit' | 'risks' | 'brief';

function initialOuterTab(hasAnalysis: boolean): OuterTab {
  const fromUrl = new URLSearchParams(window.location.search).get('maTab');
  if (fromUrl === 'cloud-portfolio') return 'cloud-portfolio';
  return hasAnalysis ? 'overview' : 'cloud-portfolio';
}

function selectOuterTab(id: OuterTab, setTab: (t: OuterTab) => void) {
  setTab(id);
  const url = new URL(window.location.href);
  if (id === 'cloud-portfolio') url.searchParams.set('maTab', 'cloud-portfolio');
  else url.searchParams.delete('maTab');
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

const OUTCOME_LABEL: Record<Outcome, string> = {
  box1: 'Box 1', box2: 'Box 2', box3: 'Box 3', box4: 'Box 4',
  consolidate: 'Consolidate', decommission: 'Decommission', queue: 'Queue',
};
const OUTCOME_ORDER: Outcome[] = ['box1', 'box2', 'box3', 'box4', 'consolidate', 'decommission', 'queue'];
const TIMING_LABEL: Record<Timing, string> = { cat1: 'Cat 1 · Immediate', cat2: 'Cat 2 · Near-term', cat3: 'Cat 3 · Deferred' };
const TIMING_ORDER: Timing[] = ['cat1', 'cat2', 'cat3'];

const SEVERITY_ORDER: RiskFlag['severity'][] = ['critical', 'high', 'medium', 'low'];
const SEVERITY_STYLE: Record<RiskFlag['severity'], { bg: string; border: string; fg: string; label: string }> = {
  critical: { bg: '#fef2f2', border: '#fecaca', fg: '#991b1b', label: 'CRITICAL' },
  high: { bg: '#fff7ed', border: '#fed7aa', fg: '#9a3412', label: 'HIGH' },
  medium: { bg: '#fffbeb', border: '#fde68a', fg: '#92400e', label: 'MEDIUM' },
  low: { bg: '#f0f9ff', border: '#bae6fd', fg: '#075985', label: 'LOW' },
};

export default function MigrationArchitectWorkspace({
  analysisMarkdown, fileName, consolidatedSummary, riskFlags, renderMarkdown,
  onExportPdf, onExportExcel, onExportPptx, onExportWord, onSendToCouncil,
  exportingExcel, exportingPptx, exportingWord, requestedTab,
}: MigrationArchitectWorkspaceProps) {
  const [tab, setTab] = useState<OuterTab>(() => initialOuterTab(Boolean(analysisMarkdown)));

  useEffect(() => {
    if (requestedTab === 'cloud-portfolio') setTab('cloud-portfolio');
  }, [requestedTab]);
  const [narrativeOpen, setNarrativeOpen] = useState(!consolidatedSummary || consolidatedSummary.length === 0);

  // The project's live disposition snapshot — same source Framework Mapping / Consolidation read,
  // so the Overview strips, the Artifacts count, and the needs-review chip never disagree with
  // what those tabs show. A second, independent fetch from the one LogicDecomposerDashboard owns
  // internally; both are cheap, and keeping them independent avoids threading a shared context
  // through a component (LogicDecomposerDashboard) that also runs standalone at /app/logic-decomposer.
  const live = useFrameworkData();

  const outcomeCounts = useMemo(() => {
    const c: Partial<Record<Outcome, number>> = {};
    for (const r of live.records) c[r.disposition] = (c[r.disposition] || 0) + 1;
    return c;
  }, [live.records]);
  const timingCounts = useMemo(() => {
    const c: Partial<Record<Timing, number>> = {};
    for (const r of live.records) c[r.timing] = (c[r.timing] || 0) + 1;
    return c;
  }, [live.records]);
  // §10.3's needs-review queue: the 0.60-0.85 confidence band + ties + gate overturns (all three
  // already folded into status `human_review` by dispositionEngine.js's routeByConfidence), plus
  // unresolved Cat 3 (status `provisional`). Reads two existing status values — nothing new here.
  const needsReview = useMemo(
    () => live.records.filter((r) => r.status === 'human_review' || r.status === 'provisional').length,
    [live.records],
  );

  // Factory operating view (task 1218070214136694, subtask 10): wave × stage from LIVE committed
  // dispositions — NOT a fabricated GCC throughput feed (the SOW's binding constraint is 6 EUCs/wk
  // GCC + 8-16wk Cat 2 2LoD, which we have no telemetry for). We only show what the disposition
  // statuses actually tell us: committed / spec-ready vs. in-review vs. unresolved, per Cat wave.
  const waveStatus = useMemo(() => {
    const mk = () => ({ committed: 0, inReview: 0, unresolved: 0, total: 0 });
    const w: Record<Timing, ReturnType<typeof mk>> = { cat1: mk(), cat2: mk(), cat3: mk() };
    for (const r of live.records) {
      const cell = w[r.timing];
      if (!cell) continue;
      cell.total += 1;
      if (r.status === 'committed' || r.status === 'auto_confirmed') cell.committed += 1;
      else if (r.status === 'human_review') cell.inReview += 1;
      else cell.unresolved += 1; // provisional / deferred / regenerate
    }
    return w;
  }, [live.records]);

  // High-friction queue: unresolved EUCs with a reason code, in SOW language. Reads existing
  // provisional_reason / status — no new scoring.
  const highFriction = useMemo(
    () => live.records
      .filter((r) => r.status === 'provisional' || r.status === 'deferred')
      .map((r) => ({
        euc_id: r.euc_id, file: r.file,
        reason: r.provisional_reason || (r.status === 'deferred' ? 'Deferred — dependency unresolved' : 'Unresolved'),
      })),
    [live.records],
  );

  const riskCount = riskFlags?.length || 0;

  // MECE tab IA (task 1218070214136694, subtask 4): one job per tab, ordered along the consultant
  // journey — Setup (Rules) → Intake/Inventory → Close gaps (Attestation) → See the plan (Framework
  // Mapping) → Deduplicate (Consolidation) → Commit (Review Queue) → Package (Deliverables) → Defend
  // (Audit). Rules / Attestation / Audit are SOW-critical (Phase 1 rubric, Phase 2 owner validation,
  // Phase 3/4 2LoD trail) and were previously unreachable when LD was embedded in MA — they now have
  // MA tabs. "Artifacts" is renamed "EUC Inventory" so it never reads as a second Portfolio next to
  // Cloud Portfolio, and to say what it is: the file-per-EUC list + workbench.
  const TABS: { id: OuterTab; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'cloud-portfolio', label: 'Cloud Portfolio' },
    { id: 'rules', label: 'Rule Configuration' },
    { id: 'artifacts', label: 'EUC Inventory', count: live.records.length || undefined },
    { id: 'attestation', label: 'Owner Attestation' },
    { id: 'framework', label: 'Framework Mapping' },
    { id: 'consolidation', label: 'Consolidation', count: live.clusters.length || undefined },
    // Item 11 (spec §5.8, §10.3) — the bulk approve/override/commit surface the needs-review
    // chip below now opens directly, instead of just parking the consultant on Framework Mapping.
    { id: 'review', label: 'Review Queue', count: needsReview || undefined },
    { id: 'deliverables', label: 'Deliverables' },
    { id: 'audit', label: 'Audit' },
    { id: 'risks', label: 'Risks & Findings', count: riskCount || undefined },
    { id: 'brief', label: 'Brief' },
  ];

  // Item 14: Deliverables is no longer a placeholder — it's the same Engagement Phases rail
  // LogicDecomposerDashboard's standalone "phases" view renders, embedded exactly like Framework
  // Mapping / Review Queue / Consolidation above it. Rules / Attestation / Audit embed the same way,
  // so the whole 10-step consultant journey is reachable from this one workspace (no need to leave
  // for /app/logic-decomposer). Overview / Risks / Brief are MA-native and render outside the embed.
  const LD_TABS: Record<string, LdView> = {
    rules: 'rules', artifacts: 'portfolio', attestation: 'attestation', framework: 'framework',
    consolidation: 'consolidation', review: 'review', deliverables: 'phases', audit: 'audit',
  };
  const showLd = tab in LD_TABS;
  const ldView: LdView = LD_TABS[tab] || 'portfolio';

  // In-screen navigation from the embedded dashboard (e.g. "Adjust framework →" → Rule
  // Configuration, the Framework Mapping consolidation chips → Consolidation) switches THIS
  // workspace's outer tab — the inverse of LD_TABS. Without this the embedded nav would be a
  // silent no-op under a controlled `view` (the MECE IA bug subtask 4 fixes).
  const navigateFromLd = (v: LdView) => {
    const entry = (Object.keys(LD_TABS) as OuterTab[]).find((t) => LD_TABS[t] === v);
    if (entry) setTab(entry);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden h-full flex flex-col">
      {/* Header — mirrors Portfolio Reporting's header actions (frontend/src/pages/PortfolioReporting.tsx). */}
      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#f59e0b' }} />
            <span className="text-sm font-semibold text-super-dark-purple">Migration Architect</span>
            {fileName && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200 truncate max-w-[240px]">
                {fileName}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {needsReview > 0 && (
              <button
                onClick={() => selectOuterTab('review', setTab)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
                title="Confidence 0.60-0.85, ties, gate overturns, or unresolved Cat 3 — spec §10.3. Opens the Review Queue, filtered to these EUCs."
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                {needsReview} need review
              </button>
            )}
            <button
              onClick={() => live.reload()}
              disabled={live.loading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-600 rounded-lg text-xs font-medium transition-colors border border-gray-200 disabled:opacity-50"
              title="Refresh the live disposition snapshot"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${live.loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={onExportPptx}
              disabled={exportingPptx}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
              title="One-click executive brief (PPTX)"
            >
              {exportingPptx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Presentation className="w-3.5 h-3.5" />}
              Generate Executive Brief
            </button>
          </div>
        </div>

        {/* Tab strip */}
        <div className="flex gap-1 mt-3 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => selectOuterTab(t.id, setTab)}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                tab === t.id ? 'bg-amber-500 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {t.label}
              {t.count != null && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                  tab === t.id ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className={`flex-1 ${tab === 'cloud-portfolio' ? 'overflow-hidden flex flex-col min-h-0' : 'overflow-y-auto'}`}>
        {tab === 'cloud-portfolio' && <CloudPortfolioTab />}

        {tab === 'overview' && (
          <div className="p-5 space-y-5">
            <div>
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Six-outcome distribution — live project state</p>
              <div className="flex flex-wrap gap-2">
                {OUTCOME_ORDER.map((k) => (
                  <button
                    key={k}
                    onClick={() => selectOuterTab('framework', setTab)}
                    className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-3.5 py-1.5 text-xs hover:bg-gray-100 transition-colors"
                  >
                    <b className="text-super-dark-purple">{outcomeCounts[k] || 0}</b>
                    <span className="text-gray-500">{OUTCOME_LABEL[k]}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Timing — Cat 1 / 2 / 3</p>
              <div className="flex flex-wrap gap-2">
                {TIMING_ORDER.map((k) => (
                  <button
                    key={k}
                    onClick={() => selectOuterTab('framework', setTab)}
                    className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-3.5 py-1.5 text-xs hover:bg-gray-100 transition-colors"
                  >
                    <b className="text-super-dark-purple">{timingCounts[k] || 0}</b>
                    <span className="text-gray-500">{TIMING_LABEL[k]}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Factory operating view (task 1218070214136694, subtask 10): wave × stage from LIVE
                committed dispositions. We show committed / in-review / unresolved per Cat wave —
                NOT a fabricated GCC build/2LoD/done throughput (no telemetry feed for that; the SOW's
                6-EUC/week + 8-16wk Cat-2 constraints are the real limits, not Stemmler capacity). */}
            <div>
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Factory waves — live disposition state</p>
              {live.records.length === 0 ? (
                <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                  <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  No inventory ingested yet — the wave board fills in once EUCs are dispositioned.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-gray-50 text-gray-500">
                        <th className="text-left font-semibold px-3 py-2">Wave</th>
                        <th className="text-right font-semibold px-3 py-2">Committed / spec-ready</th>
                        <th className="text-right font-semibold px-3 py-2">In review</th>
                        <th className="text-right font-semibold px-3 py-2">Unresolved</th>
                        <th className="text-right font-semibold px-3 py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {TIMING_ORDER.map((k) => {
                        const w = waveStatus[k];
                        return (
                          <tr key={k} className="border-t border-gray-100">
                            <td className="px-3 py-2 font-medium text-super-dark-purple">{TIMING_LABEL[k]}</td>
                            <td className="px-3 py-2 text-right text-gray-700">{w.committed}</td>
                            <td className="px-3 py-2 text-right text-amber-700">{w.inReview}</td>
                            <td className="px-3 py-2 text-right text-red-700">{w.unresolved}</td>
                            <td className="px-3 py-2 text-right font-semibold text-gray-900">{w.total}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[11px] text-gray-400 mt-1.5">
                GCC build / 2LoD / done stages aren't shown — no throughput feed exists yet, and faking a weekly rate would misrepresent the SOW's 6-EUC/week and 8–16-week Cat-2 constraints.
              </p>
            </div>

            {highFriction.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">High-friction queue — {highFriction.length}</p>
                <div className="space-y-1.5">
                  {highFriction.slice(0, 8).map((h) => (
                    <div key={h.euc_id} className="flex items-start gap-2 text-xs bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="font-mono text-red-800 flex-shrink-0">{h.euc_id}</span>
                      <span className="text-gray-600 truncate">{h.reason}</span>
                    </div>
                  ))}
                  {highFriction.length > 8 && (
                    <button onClick={() => setTab('review')} className="text-xs text-amber-700 font-semibold hover:underline">
                      + {highFriction.length - 8} more — open Review Queue
                    </button>
                  )}
                </div>
              </div>
            )}

            <div>
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Consolidated summary</p>
              {consolidatedSummary && consolidatedSummary.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr>
                        <th className="bg-super-dark-purple text-white px-3 py-2 text-left font-semibold text-xs">Artifact</th>
                        <th className="bg-super-dark-purple text-white px-3 py-2 text-left font-semibold text-xs">Path</th>
                        <th className="bg-super-dark-purple text-white px-3 py-2 text-left font-semibold text-xs">Recommendation</th>
                        <th className="bg-super-dark-purple text-white px-3 py-2 text-left font-semibold text-xs">Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consolidatedSummary.map((row, i) => (
                        <tr key={i} className="even:bg-gray-50">
                          <td className="px-3 py-2 text-xs text-gray-700 border border-gray-200 align-top font-medium">{row.artifact}</td>
                          <td className="px-3 py-2 text-xs text-gray-700 border border-gray-200 align-top">{row.path || '—'}</td>
                          <td className="px-3 py-2 text-xs text-gray-700 border border-gray-200 align-top">{row.recommendation}</td>
                          <td className="px-3 py-2 text-xs text-gray-700 border border-gray-200 align-top">{row.confidence || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                  <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  No structured consolidated summary was returned for this run — see the full narrative below.
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setNarrativeOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 text-gray-700 text-xs font-semibold hover:bg-gray-100 transition-colors"
              >
                <span>Full narrative</span>
                <ChevronRight className={`w-3.5 h-3.5 transition-transform ${narrativeOpen ? 'rotate-90' : ''}`} />
              </button>
              {narrativeOpen && <div className="p-4">{renderMarkdown(analysisMarkdown)}</div>}
            </div>
          </div>
        )}

        {tab === 'risks' && (
          <div className="p-5">
            {/* Demoted to last-run analysis (task 1218070214136694, subtask 10): these flags come
                from ONE Intelligence analysis run, not the live disposition snapshot. Labelled so a
                consultant never reads them as contradicting the live Framework Mapping / wave board
                on Overview. The live, authoritative signal is the Overview high-friction queue. */}
            <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 mb-3">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              Findings from the last analysis run{fileName ? ` (${fileName})` : ''} — a point-in-time snapshot, not the live disposition state. For the live high-friction queue, see Overview.
            </div>
            {riskFlags && riskFlags.length > 0 ? (
              <div className="space-y-2.5">
                {[...riskFlags]
                  .sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity))
                  .map((f, i) => {
                    const s = SEVERITY_STYLE[f.severity] || SEVERITY_STYLE.low;
                    return (
                      <div key={i} className="rounded-lg border px-4 py-3" style={{ background: s.bg, borderColor: s.border }}>
                        <div className="flex items-center gap-2 mb-1">
                          <ShieldAlert className="w-3.5 h-3.5" style={{ color: s.fg }} />
                          <span className="text-[10px] font-bold tracking-wider" style={{ color: s.fg }}>{s.label}</span>
                          {f.artifact && <span className="text-[10px] text-gray-500 ml-auto truncate max-w-[220px]">{f.artifact}</span>}
                        </div>
                        <p className="text-sm font-semibold" style={{ color: s.fg }}>{f.title}</p>
                        <p className="text-xs text-gray-600 mt-1 leading-relaxed">{f.detail}</p>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                No risk flags were reported for this run.
              </div>
            )}
          </div>
        )}

        {tab === 'brief' && (
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={onExportPdf}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-600 rounded-lg text-xs font-medium transition-colors border border-gray-200"
              >
                <Download className="w-3.5 h-3.5" /> PDF
              </button>
              <button
                onClick={onExportExcel}
                disabled={exportingExcel}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-green-50 text-green-700 rounded-lg text-xs font-medium transition-colors border border-green-200 disabled:opacity-50"
              >
                {exportingExcel ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />} Excel
              </button>
              <button
                onClick={onExportPptx}
                disabled={exportingPptx}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-orange-50 text-orange-700 rounded-lg text-xs font-medium transition-colors border border-orange-200 disabled:opacity-50"
              >
                {exportingPptx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Presentation className="w-3.5 h-3.5" />} PPT
              </button>
              <button
                onClick={onExportWord}
                disabled={exportingWord}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-blue-50 text-blue-700 rounded-lg text-xs font-medium transition-colors border border-blue-200 disabled:opacity-50"
              >
                {exportingWord ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Word
              </button>
              <button
                onClick={onSendToCouncil}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-xs font-medium transition-colors border border-indigo-200"
              >
                <Crown className="w-3.5 h-3.5" /> AI Council
              </button>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              {renderMarkdown(analysisMarkdown)}
            </div>
          </div>
        )}

        {/* Artifacts / Framework Mapping / Consolidation — the "as built" screens, embedded and
            kept mounted (hidden via display:none, not unmounted) so switching between them and
            back preserves LogicDecomposerDashboard's own state (open artifact panel, etc). */}
        <div style={{ display: showLd ? 'block' : 'none' }} className="p-5">
          <LogicDecomposerDashboard view={ldView} hideViewSwitcher onNavigate={navigateFromLd} />
        </div>
      </div>
    </div>
  );
}
